const BARS_MAX_WIDTH = 1920;
const BARS_ASPECT_RATIO = 220 / 1670; // SVG viewBox height/width
const BARS_MAX_HEIGHT = Math.round(BARS_MAX_WIDTH * BARS_ASPECT_RATIO); // ~253px
const HERO_PT_FLOOR = 56; // px, keeps the floor from collapsing on tiny viewports
const HERO_PT_HEADROOM = 16; // px, breathing room above the bars' own height

export const section = {
    position: 'relative',
    overflow: 'hidden',
    pt: `clamp(${HERO_PT_FLOOR}px, calc(${HERO_PT_HEADROOM}px + ${BARS_ASPECT_RATIO * 100}vw), ${HERO_PT_HEADROOM + BARS_MAX_HEIGHT}px)`,
};

export const barsWrapper = {
    position: 'absolute',
    top: { xs: 4, md: 24 },
    left: 0,
    right: 0,
    maxWidth: BARS_MAX_WIDTH,
    mx: 'auto',
    opacity: 0.9,
    pointerEvents: 'none',
    zIndex: 0,
};

// Vector SVG, so no quality loss scaling it — width: 100% / height: auto
// derives height from the SVG's own intrinsic aspect ratio. The wrapper's
// maxWidth (above) is what stops growth past BARS_MAX_WIDTH, matching pt's cap.
export const animatedBarsImage = {
    display: 'block',
    width: '100%',
    height: 'auto',
};

// pt here stacks on top of section.pt, which already clears the bars' own
// height plus headroom (see BARS_MAX_HEIGHT/HERO_PT_HEADROOM above) — xs
// stays small since the bars shrink to ~40-50px tall on narrow viewports,
// so a 48px add-on read as a disproportionately large gap underneath them.
// pb halved per EBL-13073 feedback ("Gaps per section reduce by Half").
export const container = {
    position: 'relative',
    zIndex: 1,
    pt: { xs: 2, md: 3 },
    pb: { xs: 4, md: 6 },
};

export const eyebrow = {
    display: 'block',
    color: 'var(--ecal-lightgrey)',
    mb: 2,
    fontSize: { xs: '0.72rem', md: '0.8rem' },
};

export const headline = {
    fontWeight: 700,
    lineHeight: 0.95,
    letterSpacing: '-0.02em',
    fontSize: { xs: '3rem', sm: '4rem', md: '6rem', lg: '7.5rem' },
    color: 'var(--ecal-white)',
};

export const subheadingBox = {
    mt: { xs: 2, md: 2 },
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
};

export const subheading = {
    mt: { xs: 1, md: 1 },
    maxWidth: { xs: '100%', md: '90%' },
    color: 'var(--ecal-lightgrey)',
    fontSize: { xs: '1rem', md: '1.05rem' },
    lineHeight: 1.45,
    '& strong': {
        color: 'var(--ecal-white)',
    },
};

export const subheadingMobile = {
    ...subheading,
    display: { xs: 'block', md: 'none' },
};

export const subheadingDesktop = {
    ...subheading,
    display: { xs: 'none', md: 'block' },
};

export const ctaRow = {
    mt: 3,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 1.5,
    flexDirection: { xs: 'column', md: 'row' },
};

// Gradient border wrapper — more reliable than ::before mask on MUI Button
export const whatsHotWrapper = {
    background: 'linear-gradient(90deg, #00D7FF, #0078FF)',
    borderRadius: '999px',
    padding: '1.5px',
    display: 'inline-flex',
    width: { xs: '100%', md: 'auto' },
    transition: 'transform 0.18s ease',
    '&:hover': { transform: 'translateY(-1px)' },
};

export const whatsHotButton = {
    borderRadius: '999px',
    padding: '12px 20px',
    fontSize: '16px',
    border: 'none',
    background: 'var(--ecal-navy)',
    color: 'var(--ecal-white)',
    fontWeight: 600,
    width: '100%',
    '&:hover': {
        background: 'var(--ecal-navy)',
        border: 'none',
    },
};

export const whatsHotIcon = {
    color: 'var(--ecal-orange)',
};

export const liveIndicator = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    ml: 1,
    color: 'var(--ecal-lightgrey)',
    fontSize: '0.875rem',
};

// One continuous gradient sweep across "colour." (period included).
export const colourWordStyle = {
    background: 'linear-gradient(100deg, #FF4D00 0%, #FF9E00 20%, #FFDF10 40%, #00D456 60%, #0078FF 80%, #00D7FF 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    display: 'inline-block',
};

export const gradientCool = {
    background: 'linear-gradient(90deg, #00D7FF 0%, #0078FF 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
};

export const liveDot = {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '999px',
    background: 'linear-gradient(90deg, #00D456, #00D7FF)',
    flexShrink: 0,
    animation: 'pulseGlow 1.6s ease-out infinite',
    '@keyframes pulseGlow': {
        '0%, 100%': { boxShadow: '0 0 0 0 rgba(0, 215, 255, 0.4)' },
        '50%':       { boxShadow: '0 0 0 12px rgba(0, 215, 255, 0)' },
    },
};
